/**
 * HTML → Lumen block model (P-521) and block model → HTML (P-525). Pure code: a small tolerant HTML
 * parser, so imports run without a browser DOM.
 */
import { type MarkdownExportOptions, type MdBlock, type MdInline, type MdStyles, type MdTableContent } from './markdown';

interface El {
  tag: string;
  attrs: Record<string, string>;
  children: Node[];
}
type Node = El | string;

const VOID = new Set(['br', 'hr', 'img', 'input', 'meta', 'link', 'source', 'col', 'area', 'base', 'embed', 'wbr']);
const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', mdash: '—', ndash: '–', hellip: '…', rsquo: '’', lsquo: '‘', rdquo: '”', ldquo: '“' };

export function decodeEntities(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, e: string) => {
    if (e[0] === '#') return String.fromCodePoint(e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10));
    return ENTITIES[e.toLowerCase()] ?? m;
  });
}

/** Parses HTML into a light element tree (unknown closing tags are ignored, unclosed ones auto-close). */
export function parseHtml(html: string): El {
  const root: El = { tag: '#root', attrs: {}, children: [] };
  const stack: El[] = [root];
  const re = /<!--[\s\S]*?-->|<!doctype[^>]*>|<\/?([a-zA-Z][\w:-]*)((?:\s+[^\s=>/]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+))?)*)\s*\/?>|([^<]+|<)/gi;
  let m: RegExpExecArray | null;
  const top = () => stack[stack.length - 1]!;
  while ((m = re.exec(html))) {
    const [full, tagName, attrText, textPart] = m;
    if (textPart !== undefined) {
      top().children.push(decodeEntities(textPart));
      continue;
    }
    if (!tagName) continue;
    const tag = tagName.toLowerCase();
    if (full.startsWith('</')) {
      const idx = stack.map((e) => e.tag).lastIndexOf(tag);
      if (idx > 0) stack.length = idx;
      continue;
    }
    const attrs: Record<string, string> = {};
    for (const a of (attrText ?? '').matchAll(/([^\s=>/]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g)) attrs[a[1]!.toLowerCase()] = decodeEntities(a[2] ?? a[3] ?? a[4] ?? '');
    // Implicitly closed elements.
    if ((tag === 'li' && top().tag === 'li') || (tag === 'p' && top().tag === 'p') || ((tag === 'td' || tag === 'th') && (top().tag === 'td' || top().tag === 'th')) || (tag === 'tr' && top().tag === 'tr')) stack.pop();
    const el: El = { tag, attrs, children: [] };
    top().children.push(el);
    if (!VOID.has(tag) && !full.endsWith('/>') && tag !== 'script' && tag !== 'style') stack.push(el);
    if (tag === 'script' || tag === 'style') {
      const end = html.toLowerCase().indexOf(`</${tag}`, re.lastIndex);
      re.lastIndex = end < 0 ? html.length : html.indexOf('>', end) + 1;
    }
  }
  return root;
}

const BLOCK_TAGS = new Set(['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'pre', 'hr', 'table', 'img', 'figure', 'details', 'aside', 'section', 'article', 'header', 'footer', 'main', 'body', 'html', 'figcaption', 'summary']);

function textOf(n: Node): string {
  return typeof n === 'string' ? n : n.tag === 'br' ? '\n' : n.children.map(textOf).join('');
}

function inlineFrom(nodes: Node[], styles: MdStyles = {}, out: MdInline[] = []): MdInline[] {
  for (const n of nodes) {
    if (typeof n === 'string') {
      const t = n.replace(/\s+/g, ' ');
      if (!t) continue;
      const last = out[out.length - 1];
      if (last?.type === 'text' && JSON.stringify(last.styles) === JSON.stringify(styles)) last.text += t;
      else out.push({ type: 'text', text: t, styles: { ...styles } });
      continue;
    }
    if (n.tag === 'br') {
      inlineFrom(['\n'], styles, out);
      const last = out[out.length - 1];
      if (last?.type === 'text') last.text = last.text.replace(/ $/, '') + '\n';
      continue;
    }
    if (n.tag === 'a' && n.attrs.href) {
      const content = inlineFrom(n.children, styles).filter((x): x is Extract<MdInline, { type: 'text' }> => x.type === 'text');
      out.push({ type: 'link', href: n.attrs.href, content: content.length ? content : [{ type: 'text', text: n.attrs.href, styles: { ...styles } }] });
      continue;
    }
    const next = { ...styles };
    if (n.tag === 'strong' || n.tag === 'b') next.bold = true;
    if (n.tag === 'em' || n.tag === 'i') next.italic = true;
    if (n.tag === 'u' || n.tag === 'ins') next.underline = true;
    if (n.tag === 's' || n.tag === 'del' || n.tag === 'strike') next.strike = true;
    if (n.tag === 'code' || n.tag === 'kbd') next.code = true;
    const style = n.attrs.style ?? '';
    if (/font-weight:\s*(bold|[6-9]00)/.test(style)) next.bold = true;
    if (/font-style:\s*italic/.test(style)) next.italic = true;
    if (/text-decoration:[^;]*underline/.test(style)) next.underline = true;
    if (/text-decoration:[^;]*line-through/.test(style)) next.strike = true;
    inlineFrom(n.children, next, out);
  }
  return out;
}

function trimInline(content: MdInline[]): MdInline[] {
  const out = content.map((c) => (c.type === 'text' ? { ...c } : c));
  const first = out[0];
  if (first?.type === 'text') first.text = first.text.replace(/^\s+/, '');
  const last = out[out.length - 1];
  if (last?.type === 'text') last.text = last.text.replace(/\s+$/, '');
  return out.filter((c) => c.type !== 'text' || c.text !== '');
}

function isBlock(n: Node): boolean {
  return typeof n !== 'string' && BLOCK_TAGS.has(n.tag);
}

/** Converts children that mix inline content and blocks: inline runs become paragraphs. */
function blocksFrom(nodes: Node[]): MdBlock[] {
  const out: MdBlock[] = [];
  let run: Node[] = [];
  const flush = () => {
    const content = trimInline(inlineFrom(run));
    if (content.length) out.push({ type: 'paragraph', content });
    run = [];
  };
  for (const n of nodes) {
    if (!isBlock(n)) {
      run.push(n);
      continue;
    }
    flush();
    out.push(...blockFrom(n as El));
  }
  flush();
  return out;
}

function listItems(el: El, type: 'bulletListItem' | 'numberedListItem'): MdBlock[] {
  return el.children
    .filter((c): c is El => typeof c !== 'string' && c.tag === 'li')
    .map((li) => {
      const inline = li.children.filter((c) => !isBlock(c) && !(typeof c !== 'string' && c.tag === 'input'));
      const checkbox = li.children.find((c): c is El => typeof c !== 'string' && c.tag === 'input' && c.attrs.type === 'checkbox');
      const nested = li.children.filter((c) => isBlock(c));
      // A <li> whose text is in a <p> keeps the first paragraph as its text.
      let content = trimInline(inlineFrom(inline));
      let kids = nested;
      if (!content.length && nested[0] && typeof nested[0] !== 'string' && nested[0].tag === 'p') {
        content = trimInline(inlineFrom(nested[0].children));
        kids = nested.slice(1);
      }
      const block: MdBlock = checkbox ? { type: 'checkListItem', props: { checked: 'checked' in checkbox.attrs }, content } : { type, content };
      const children = blocksFrom(kids);
      if (children.length) block.children = children;
      return block;
    });
}

function blockFrom(el: El): MdBlock[] {
  switch (el.tag) {
    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'h6':
      return [{ type: 'heading', props: { level: Math.min(3, Number(el.tag[1])) }, content: trimInline(inlineFrom(el.children)) }];
    case 'p':
      return blocksFrom(el.children);
    case 'ul':
      return listItems(el, 'bulletListItem');
    case 'ol':
      return listItems(el, 'numberedListItem');
    case 'li':
      return listItems({ tag: 'ul', attrs: {}, children: [el] }, 'bulletListItem');
    case 'blockquote': {
      const inner = blocksFrom(el.children);
      if (inner.length === 1 && inner[0]!.type === 'paragraph') return [{ type: 'quote', content: inner[0]!.content }];
      const [first, ...rest] = inner;
      const quote: MdBlock = { type: 'quote', content: first?.type === 'paragraph' ? first.content : [] };
      const children = first?.type === 'paragraph' ? rest : inner;
      if (children.length) quote.children = children;
      return [quote];
    }
    case 'pre': {
      const code = el.children.find((c): c is El => typeof c !== 'string' && c.tag === 'code');
      const lang = /language-([\w+#-]+)/.exec(code?.attrs.class ?? el.attrs.class ?? '')?.[1] ?? 'text';
      const raw = textOf(code ?? el).replace(/\n$/, '');
      return [{ type: 'codeBlock', props: { language: lang }, content: raw ? [{ type: 'text', text: raw, styles: {} }] : [] }];
    }
    case 'hr':
      return [{ type: 'divider' }];
    case 'img':
      return el.attrs.src ? [{ type: 'image', props: { url: el.attrs.src, caption: el.attrs.alt ?? '' } }] : [];
    case 'figure': {
      const img = findTag(el, 'img');
      const caption = findTag(el, 'figcaption');
      if (img) return [{ type: 'image', props: { url: img.attrs.src ?? '', caption: caption ? textOf(caption).trim() : (img.attrs.alt ?? '') } }];
      return blocksFrom(el.children);
    }
    case 'details': {
      const summary = el.children.find((c): c is El => typeof c !== 'string' && c.tag === 'summary');
      const block: MdBlock = { type: 'toggleListItem', content: summary ? trimInline(inlineFrom(summary.children)) : [] };
      const children = blocksFrom(el.children.filter((c) => c !== summary));
      if (children.length) block.children = children;
      return [block];
    }
    case 'aside': {
      const inner = blocksFrom(el.children);
      const first = inner[0]?.type === 'paragraph' ? inner.shift()! : null;
      const content = (first?.content as MdInline[] | undefined) ?? [];
      let icon = '💡';
      const lead = content[0];
      if (lead?.type === 'text') {
        const e = /^(\p{Extended_Pictographic}️?)\s*/u.exec(lead.text);
        if (e) {
          icon = e[1]!;
          lead.text = lead.text.slice(e[0].length);
        }
      }
      const block: MdBlock = { type: 'callout', props: { icon }, content };
      if (inner.length) block.children = inner;
      return [block];
    }
    case 'table': {
      const rows = findAll(el, 'tr').map((tr) => tr.children.filter((c): c is El => typeof c !== 'string' && (c.tag === 'td' || c.tag === 'th')));
      if (!rows.length) return [];
      const content: MdTableContent = {
        type: 'tableContent',
        headerRows: rows[0]!.some((c) => c.tag === 'th') ? 1 : 0,
        rows: rows.map((r) => ({ cells: r.map((c) => ({ type: 'tableCell' as const, content: trimInline(inlineFrom(c.children)), props: {} })) })),
      };
      return [{ type: 'table', content }];
    }
    case 'summary':
    case 'figcaption':
      return [];
    default:
      return blocksFrom(el.children);
  }
}

function findTag(el: El, tag: string): El | null {
  for (const c of el.children) {
    if (typeof c === 'string') continue;
    if (c.tag === tag) return c;
    const f = findTag(c, tag);
    if (f) return f;
  }
  return null;
}

function findAll(el: El, tag: string, out: El[] = []): El[] {
  for (const c of el.children) {
    if (typeof c === 'string') continue;
    if (c.tag === tag) out.push(c);
    else findAll(c, tag, out);
  }
  return out;
}

/** HTML → block model. Returns the document title (`<title>` or a leading `<h1>`) separately. */
export function htmlToBlocks(html: string): { title: string | null; blocks: MdBlock[] } {
  const root = parseHtml(html);
  const titleEl = findTag(root, 'title');
  const body = findTag(root, 'body') ?? root;
  const blocks = blocksFrom(body.children.filter((c) => !(typeof c !== 'string' && (c.tag === 'head' || c.tag === 'title'))));
  let title = titleEl ? textOf(titleEl).trim() || null : null;
  const first = blocks[0];
  if (first?.type === 'heading' && first.props?.level === 1) {
    const t = (first.content as MdInline[]).map((c) => (c.type === 'text' ? c.text : '')).join('');
    if (!title || t === title) {
      title = t;
      blocks.shift();
    }
  }
  return { title, blocks };
}

// ───────────── export ─────────────

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function inlineHtml(content: MdInline[] | undefined, opts: MarkdownExportOptions): string {
  return (content ?? [])
    .map((c) => {
      if (c.type === 'link') return `<a href="${esc(c.href)}">${inlineHtml(c.content, opts)}</a>`;
      if (c.type === 'inlineEquation') return `<span class="equation">${esc(c.props.latex)}</span>`;
      if (c.type === 'mention') {
        if (c.props.kind === 'date') return `<time>${esc(c.props.date ?? '')}</time>`;
        const l = opts.pageLink?.(c.props.id ?? '');
        return l ? `<a href="${esc(l.href)}">${esc(l.title)}</a>` : '';
      }
      let t = esc(c.text).replace(/\n/g, '<br>');
      const s = c.styles ?? {};
      if (s.code) t = `<code>${t}</code>`;
      if (s.underline) t = `<u>${t}</u>`;
      if (s.strike) t = `<s>${t}</s>`;
      if (s.italic) t = `<em>${t}</em>`;
      if (s.bold) t = `<strong>${t}</strong>`;
      return t;
    })
    .join('');
}

function blockHtml(b: MdBlock, opts: MarkdownExportOptions): string {
  const p = b.props ?? {};
  const inline = Array.isArray(b.content) ? inlineHtml(b.content, opts) : '';
  const kids = b.children?.length ? blocksToHtml(b.children, opts) : '';
  switch (b.type) {
    case 'heading':
      return `<h${Math.min(3, Number(p.level ?? 1))}>${inline}</h${Math.min(3, Number(p.level ?? 1))}>${kids}`;
    case 'bulletListItem':
      return `<ul><li>${inline}${kids}</li></ul>`;
    case 'numberedListItem':
      return `<ol><li>${inline}${kids}</li></ol>`;
    case 'checkListItem':
      return `<ul class="to-do"><li><input type="checkbox"${p.checked ? ' checked' : ''}> ${inline}${kids}</li></ul>`;
    case 'toggleListItem':
      return `<details open><summary>${inline}</summary>${kids}</details>`;
    case 'quote':
      return `<blockquote>${inline}${kids}</blockquote>`;
    case 'codeBlock': {
      const raw = Array.isArray(b.content) ? b.content.map((c) => (c.type === 'text' ? c.text : '')).join('') : '';
      return `<pre><code class="language-${esc(String(p.language ?? 'text'))}">${esc(raw)}</code></pre>`;
    }
    case 'divider':
      return '<hr>';
    case 'callout':
      return `<aside class="callout">${esc(String(p.icon ?? ''))} ${inline}${kids}</aside>`;
    case 'equation':
      return `<div class="equation">${esc(String(p.latex ?? ''))}</div>`;
    case 'image':
      return `<figure><img src="${esc(String(p.url ?? ''))}" alt="${esc(String(p.caption ?? ''))}">${p.caption ? `<figcaption>${esc(String(p.caption))}</figcaption>` : ''}</figure>`;
    case 'video':
    case 'audio':
    case 'file':
    case 'pdf':
    case 'bookmark':
    case 'embed':
      return p.url ? `<p><a href="${esc(String(p.url))}">${esc(String(p.name || p.title || p.url))}</a></p>` : '';
    case 'page':
    case 'linkToPage': {
      const l = opts.pageLink?.(String(p.pageId ?? ''));
      return l ? `<p class="link-to-page"><a href="${esc(l.href)}">${esc(l.title)}</a></p>` : '';
    }
    case 'database': {
      const l = opts.databaseLink?.(String(p.dbId ?? ''));
      return l ? `<p><a href="${esc(l.href)}">${esc(l.title)}</a></p>` : '';
    }
    case 'table': {
      const t = b.content as MdTableContent | undefined;
      if (!t) return '';
      return `<table>${t.rows
        .map((r, i) => `<tr>${r.cells.map((c) => {
          const tag = i < (t.headerRows ?? 0) ? 'th' : 'td';
          return `<${tag}>${inlineHtml(Array.isArray(c) ? c : c.content, opts)}</${tag}>`;
        }).join('')}</tr>`)
        .join('')}</table>`;
    }
    case 'columnList':
      return `<div class="column-list">${kids}</div>`;
    case 'column':
      return `<div class="column">${kids}</div>`;
    case 'syncedBlock':
      return kids;
    case 'toc':
    case 'breadcrumb':
    case 'button':
      return '';
    default:
      return `<p>${inline}</p>${kids ? `<div class="indented">${kids}</div>` : ''}`;
  }
}

export function blocksToHtml(blocks: MdBlock[], opts: MarkdownExportOptions = {}): string {
  return blocks.map((b) => blockHtml(b, opts)).join('\n');
}

/** A standalone HTML document for a page (P-525). */
export function pageToHtml(title: string, blocks: MdBlock[], opts: MarkdownExportOptions = {}): string {
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>${esc(title)}</title>
<style>
body { max-width: 720px; margin: 48px auto; padding: 0 24px; font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif; line-height: 1.5; color: rgb(55, 53, 47); }
pre { background: rgb(247, 246, 243); padding: 16px; border-radius: 4px; overflow: auto; }
blockquote { border-left: 3px solid currentColor; margin: 0; padding-left: 14px; }
aside.callout { display: block; background: rgb(241, 241, 239); padding: 16px; border-radius: 4px; }
ul.to-do { list-style: none; padding-left: 0; }
table { border-collapse: collapse; } td, th { border: 1px solid rgb(233, 233, 231); padding: 6px 8px; }
.column-list { display: flex; gap: 24px; } .column { flex: 1; }
.indented { padding-left: 24px; }
img { max-width: 100%; }
</style>
</head>
<body>
<h1>${esc(title)}</h1>
${blocksToHtml(blocks, opts)}
</body>
</html>
`;
}

